import { FC, ReactNode, SVGProps } from 'react'

import AnalyticsIcon from 'src/Assets/Svg/upsell/Analysis.svg?react'
import ReachIcon from 'src/Assets/Svg/upsell/Reach.svg?react'
import IncreaseIcon from 'src/Assets/Svg/common/increase.svg?react'

import {
  Accent,
  ButtonContent,
  CTAStyledButton,
  StyledGreyTextButton,
} from './styles'

export enum IntercomMessageEnum {
  REPORT_CONCERN = 'Report a Concern',
  REQUEST_ANALYSIS = 'Request Campaign Analysis',
}

type UpsellVariant = 'negative' | 'positive' | 'excellent'

type IconComponent = FC<SVGProps<SVGSVGElement>>

export type CampaignUpsellProps = {
  onUpsellMetaAds: () => void
  onOpenIntercom: (message: IntercomMessageEnum) => void
  onRelaunch: (isCTA?: boolean) => void
  placementsCount: number
  isRelaunchAvailable?: boolean
  campaignId?: number
}

export const getUpsellVariant = (placementsCount: number): UpsellVariant => {
  if (placementsCount === 0) {
    return 'negative'
  }

  if (placementsCount <= 2) {
    return 'positive'
  }

  return 'excellent'
}

type UpsellConfig = {
  title: string
  description: ReactNode
  buttonText?: string
  Icon?: IconComponent
  buttonType?: 'greyText' | 'greenText'
  isHighlighted?: boolean
  buttonId?: string
  onClick?: (props: CampaignUpsellProps) => void
  shouldShowButton?: (props: CampaignUpsellProps) => boolean
}
export const upsellConfig: Record<UpsellVariant, UpsellConfig> = {
  negative: {
    title: 'No placements this time',
    description: (
      <>
        <p>
          Your track got attention, but it was not picked by curators in this
          campaign. This can happen even with good music.
        </p>
        <p>
          Let’s review your campaign and find what can be improved for your next
          submission.
        </p>
      </>
    ),
    buttonText: 'Get free analysis',
    Icon: AnalyticsIcon,
    buttonType: 'greyText',
    buttonId: 'request',
    onClick: ({ onOpenIntercom }) =>
      onOpenIntercom(IntercomMessageEnum.REQUEST_ANALYSIS),
  },
  positive: {
    title: 'Your track has great potential!',
    description: (
      <>
        <p>
          Curators are responding well to your music, and your campaign is
          already showing positive signs.
        </p>
        <p>
          Now is a good time to build on that momentum with Meta Ads, reach more
          listeners, and create more activity around your track. More streams,
          saves, and engagement can help trigger Spotify’s algorithm and
          increase your chances of organic growth.
        </p>
      </>
    ),
    buttonText: 'Reach more listeners',
    Icon: IncreaseIcon,
    buttonType: 'greenText',
    isHighlighted: true,
    onClick: ({ onUpsellMetaAds }) => onUpsellMetaAds(),
  },
  excellent: {
    title: 'Your track received strong curator interest!',
    description: (
      <>
        <p>
          Your track performed better than{' '}
          <Accent>60% of submissions this month</Accent>, which means curators
          are responding well to your music.
        </p>
        <p>
          Now is the right time to build on this momentum, reach more relevant
          curators, and increase your chances of getting more playlist adds.
        </p>
      </>
    ),
    buttonText: 'Pitch more curators',
    Icon: ReachIcon,
    buttonType: 'greenText',
    isHighlighted: true,
    onClick: ({ onRelaunch }) => onRelaunch(true),
    shouldShowButton: ({ isRelaunchAvailable }) => Boolean(isRelaunchAvailable),
  },
}

export const renderButton = (
  config: UpsellConfig,
  props: CampaignUpsellProps,
): ReactNode => {
  const { buttonText, Icon, buttonType, buttonId, onClick } = config

  if (!buttonText || !Icon || !onClick) {
    return null
  }

  const content = (
    <ButtonContent $onCta={buttonType === 'greenText'}>
      <Icon />
      <span>{buttonText}</span>
    </ButtonContent>
  )

  const handleClick = (): void => onClick(props)

  if (buttonType === 'greenText') {
    return <CTAStyledButton onClick={handleClick}>{content}</CTAStyledButton>
  }

  return (
    <StyledGreyTextButton id={buttonId} onClick={handleClick}>
      {content}
    </StyledGreyTextButton>
  )
}
